/* AddPatientDialog — iter97.87
 *
 * Reusable "Add a patient" modal extracted out of the Doctor Patients
 * page so it can be re-mounted in any flow that needs to quickly seed
 * a fresh patient without leaving the screen. Right now it's wired
 * in:
 *
 *   • /doctor/patients   → original entry point (header CTA)
 *   • /doctor/cases/new  → "Add a new Patient" inline link in Step 1
 *
 * Props
 * -----
 * open: boolean — controls modal open state
 * onOpenChange(boolean): void — called when user closes the dialog
 * onCreated(patient): void — called with the newly-created patient
 *                            doc after a successful POST. The host
 *                            decides what to do next (refresh a list,
 *                            auto-select the patient in a picker, etc).
 */
import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import PhoneInput10 from "@/components/site/PhoneInput10";
import { toE164, isValid10Digit } from "@/lib/phone";
import { resizePatientPhoto } from "@/lib/resizePatientPhoto";
import { Camera, Trash } from "@phosphor-icons/react";

const EMPTY = { name: "", phone: "", email: "", dob: "", gender: "", notes: "" };
/* iter98.99dn — Single canonical gender option list shared with
   PatientProfile's DetailsTab so the value the doctor picks here
   resolves correctly when the About tab re-renders the dropdown.
   Previously this file used capitalized labels ("Male"/"Female")
   while DetailsTab used lowercase snake_case values
   ("male"/"female"/"non_binary"/"prefer_not_to_say"), so the saved
   value never matched any <option> on the About tab and the
   dropdown silently fell back to "Not set". */
const GENDER_OPTIONS = [
    { value: "", label: "Select…" },
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "non_binary", label: "Non-binary" },
    { value: "prefer_not_to_say", label: "Prefer not to say" },
];

export default function AddPatientDialog({ open, onOpenChange, onCreated, defaultName = "" }) {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    /* iter101.58 — Optional profile photo captured at create-time. Kept
       as a local base64 preview until the create call succeeds — then
       posted to /doctor/patients/{id}/photo in a follow-up request so
       the doctor doesn't have to bounce into the patient profile just
       to add the picture. */
    const [photoDataUrl, setPhotoDataUrl] = useState("");
    const [resizingPhoto, setResizingPhoto] = useState(false);
    const photoInputRef = useRef(null);

    // Reset whenever the dialog closes so the next open starts fresh.
    useEffect(() => {
        if (!open) {
            setForm(EMPTY);
            setPhotoDataUrl("");
            setSaving(false);
        } else if (defaultName) {
            // iter98.99y — Prefill the name when launched from the Billing
            // picker's "+ Create [name]" CTA. The doctor already typed a
            // name they couldn't find; we don't make them type it again.
            setForm((prev) => ({ ...prev, name: defaultName }));
        }
    }, [open, defaultName]);

    const onPhotoPick = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setResizingPhoto(true);
        try {
            const dataUrl = await resizePatientPhoto(file, 256);
            setPhotoDataUrl(dataUrl);
        } catch (err) {
             
            console.error("Failed to prepare photo", err);
            toast.error("Couldn't prepare that image — try a different file.");
        } finally {
            setResizingPhoto(false);
            if (photoInputRef.current) photoInputRef.current.value = "";
        }
    };

    const submit = async () => {
        if ((form.name || "").trim().length < 2) { toast.error("Enter a valid name"); return; }
        if (!isValid10Digit(form.phone)) {
            toast.error("Enter the patient's 10-digit Indian mobile number");
            return;
        }
        setSaving(true);
        // iter97.88 — Strict split between (a) the POST itself and (b)
        // the host's onCreated handler. If the POST fails, we surface
        // the backend detail (e.g. "That phone belongs to a non-patient
        // account") instead of a vague "Failed". If the POST succeeds
        // but the host's onCreated throws (e.g. ReferenceError, stale
        // setter), we still close the dialog cleanly and never flash a
        // misleading error — the patient really did get created.
        let created;
        try {
            const r = await api.post("/doctor/patients", { ...form, phone: toE164(form.phone) });
            created = r.data;
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" && detail ? detail : "Could not save patient — please try again");
            setSaving(false);
            return;
        }

        /* iter101.58 — Attach the photo now that we have the patient id.
           Deliberately not fatal — if the photo call fails (network
           blip, oversized image), the patient is still safely created
           and the doctor gets a soft warning + can re-upload from the
           profile page. */
        if (photoDataUrl) {
            try {
                const photoResp = await api.put(
                    `/doctor/patients/${created.id}/photo`,
                    { photo_data_url: photoDataUrl },
                );
                created = { ...created, photo_data_url: photoResp.data?.photo_data_url || photoDataUrl };
            } catch (photoErr) {
                 
                console.error("Photo upload failed after create:", photoErr);
                toast.warning("Patient saved, but the photo couldn't be uploaded — you can add it from the patient's profile.");
            }
        }

        toast.success(`${created.name} added to your patients`);
        try {
            onCreated?.(created);
        } catch (hostErr) {
            // Host handler crashed AFTER a successful create. Log it so
            // we don't swallow real bugs silently, but the doctor's UX
            // shouldn't suffer for it.
             
            console.error("AddPatientDialog onCreated handler threw:", hostErr);
        }
        onOpenChange?.(false);
        setSaving(false);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onOpenChange?.(false)}>
            <DialogContent className="max-w-md" data-testid="add-patient-dialog">
                <DialogHeader>
                    <DialogTitle>Add a patient</DialogTitle>
                    <DialogDescription>
                        Use this when a patient walks in and you want them in your portal without going through the website booking flow.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                    {/* iter101.58 — Optional profile photo tile. Doctors
                        used to have to save the patient and then bounce
                        into the profile page just to attach a picture;
                        capturing it here at create-time removes that
                        step. Base64 preview held locally until the POST
                        succeeds, then attached via PUT /photo. */}
                    <div className="flex items-center gap-4 pb-3 border-b border-border/60">
                        <button
                            type="button"
                            onClick={() => photoInputRef.current?.click()}
                            disabled={resizingPhoto || saving}
                            className="relative w-16 h-16 rounded-full overflow-hidden ring-2 ring-primary/15 hover:ring-primary/40 transition grid place-items-center shrink-0 focus-visible:outline-none focus-visible:ring-primary/50"
                            style={photoDataUrl ? {} : { background: "linear-gradient(135deg, hsl(280 60% 55%), hsl(200 70% 50%))" }}
                            title={photoDataUrl ? "Replace patient photo" : "Add patient photo"}
                            data-testid="add-patient-photo-btn"
                        >
                            {photoDataUrl ? (
                                <img src={photoDataUrl} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <Camera size={22} weight="duotone" className="text-white" />
                            )}
                        </button>
                        <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-foreground">
                                {photoDataUrl ? "Photo attached" : "Add a profile photo"}
                            </div>
                            <div className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                                Optional. Square crop, resized to 256&#8239;px before upload. You can always change it later from the patient&apos;s profile.
                            </div>
                            {(photoDataUrl || resizingPhoto) && (
                                <div className="mt-1.5 flex items-center gap-3 text-[11px]">
                                    {resizingPhoto ? (
                                        <span className="text-muted-foreground">Preparing…</span>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => photoInputRef.current?.click()}
                                                className="text-primary font-semibold hover:underline"
                                                data-testid="add-patient-photo-replace-btn"
                                            >
                                                Replace
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setPhotoDataUrl("")}
                                                className="text-muted-foreground hover:text-destructive inline-flex items-center gap-1"
                                                data-testid="add-patient-photo-remove-btn"
                                            >
                                                <Trash size={11} /> Remove
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        <input
                            ref={photoInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={onPhotoPick}
                            data-testid="add-patient-photo-input"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Full name</Label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10 mt-1" data-testid="doctor-patient-form-name" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Phone (10-digit Indian mobile)</Label>
                        <div className="h-10 mt-1">
                            <PhoneInput10
                                value={form.phone}
                                onChange={(v) => setForm({ ...form, phone: v })}
                                inputClassName="h-10"
                                testid="doctor-patient-form-phone"
                            />
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1">If this phone already has a patient account, we&apos;ll link it to your clinic.</p>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Email (optional)</Label>
                        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-10 mt-1" data-testid="doctor-patient-form-email" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-xs font-semibold">Date of birth</Label>
                            <Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="h-10 mt-1" data-testid="doctor-patient-form-dob" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Gender</Label>
                            <select
                                value={form.gender}
                                onChange={(e) => setForm({ ...form, gender: e.target.value })}
                                className="w-full h-10 mt-1 text-sm rounded-lg border border-border bg-background px-2"
                                data-testid="doctor-patient-form-gender"
                            >
                                {GENDER_OPTIONS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Notes (optional)</Label>
                        <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" placeholder="e.g. Referred by Dr. Mehta, prefers weekend slots." data-testid="doctor-patient-form-notes" />
                    </div>
                </div>
                <DialogFooter className="mt-3">
                    <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={saving}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} data-testid="doctor-patient-form-save">
                        {saving ? "Saving…" : "Add patient"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
